import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('cit_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('cit_token');
      localStorage.removeItem('cit_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (email, password) => api.post('/auth/login', { email, password });
export const register = (data) => api.post('/auth/register', data);
export const getMe = () => api.get('/auth/me');

// Deals
export const getDeals = (params) => api.get('/deals', { params });
export const getDeal = (id) => api.get(`/deals/${id}`);
export const createDeal = (data) => api.post('/deals', data);
export const updateDeal = (id, data) => api.put(`/deals/${id}`, data);
export const deleteDeal = (id) => api.delete(`/deals/${id}`);
export const getDefaultStipulations = () => api.get('/stipulations/defaults');

// Deal Lifecycle
export const toggleDealFunding = (dealId, funded) => api.post(`/deals/${dealId}/funding?funded=${funded}`);
export const archiveDeal = (dealId) => api.post(`/deals/${dealId}/archive`);
export const bulkArchiveDeals = (dealIds) => api.post('/deals/bulk-archive', { deal_ids: dealIds });
export const unarchiveDeal = (dealId) => api.post(`/deals/${dealId}/unarchive`);
export const unwindDeal = (dealId, reason, reasonOther) => api.post(`/deals/${dealId}/unwind`, { reason, reason_other: reasonOther });
export const reverseUnwindDeal = (dealId) => api.post(`/deals/${dealId}/reverse-unwind`);
export const getArchivedDeals = () => api.get('/deals/archived');
export const getUnwoundDeals = (params = {}) => api.get('/deals/unwound', { params });
export const getFundedDeals = (params = {}) => api.get('/deals/funded', { params });
export const exportAllDealsCsv = () => api.get('/deals/export-csv', { responseType: 'blob' });
export const testNightlyExport = () => api.post('/export/nightly-test');
export const getNightlyExportStatus = () => api.get('/export/nightly-status');

// Stipulations
export const getDealStips = (dealId) => api.get(`/deals/${dealId}/stips`);
export const uploadStip = (data) => api.post(`/deals/${data.deal_id}/stips/upload`, data);
export const verifyStip = (dealId, stipName) => api.post(`/deals/${dealId}/stips/verify?stip_name=${encodeURIComponent(stipName)}`);
export const getMissingStips = () => api.get('/stips/missing');
export const addStipToDeal = (dealId, stipName, isCustom = false) => api.post(`/deals/${dealId}/stips/add`, { stip_name: stipName, is_custom: isCustom });
export const removeStipFromDeal = (dealId, stipName) => api.delete(`/deals/${dealId}/stips/remove`, { data: { stip_name: stipName } });
export const bulkUpdateStipStatus = (dealId, stipNames, status) => api.post(`/deals/${dealId}/stips/bulk-status`, { stip_names: stipNames, status });

// Deal Notes (Internal and Lender-Facing)
export const getDealNotes = (dealId, noteType = null) => {
  const params = noteType ? { note_type: noteType } : {};
  return api.get(`/deals/${dealId}/notes`, { params });
};
export const createDealNote = (dealId, noteText, noteType = 'internal', category = null) => 
  api.post(`/deals/${dealId}/notes`, { note_text: noteText, note_type: noteType, category });
export const updateDealNote = (dealId, noteId, noteText, noteType = null, category = null) => 
  api.put(`/deals/${dealId}/notes/${noteId}`, { note_text: noteText, note_type: noteType, category });
export const deleteDealNote = (dealId, noteId) => api.delete(`/deals/${dealId}/notes/${noteId}`);
export const adminDeleteNote = (noteId) => api.delete(`/notes/${noteId}`);
export const cleanupOrphanedNotes = () => api.post('/notes/cleanup-orphans');
export const togglePinNote = (dealId, noteId) => api.put(`/deals/${dealId}/notes/${noteId}/pin`);
export const getPinnedNotes = () => api.get('/notes/pinned');

// Interviews
export const getInterviews = (params) => api.get('/interviews', { params });
export const getInterviewStats = () => api.get('/interviews/stats');
export const getOverdueInterviews = () => api.get('/interviews/overdue');
export const scheduleInterview = (data) => api.post('/interviews/schedule', data);
export const completeInterview = (dealId) => api.post(`/interviews/${dealId}/complete`);
export const sendInterviewReminder = (dealId) => api.post(`/interviews/${dealId}/reminder`);
export const rescheduleInterview = (dealId, data) => api.post(`/interviews/${dealId}/reschedule`, data);
export const getInterviewCalendar = (params) => api.get('/interviews/calendar', { params });

// Notifications
export const getNotifications = (unreadOnly) => api.get('/notifications', { params: { unread_only: unreadOnly } });
export const markNotificationRead = (id) => api.post(`/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.post('/notifications/read-all');

// Activity
export const getActivityLogs = (params) => api.get('/activity', { params });

// Settings
export const getSettings = () => api.get('/settings');
export const updateSettings = (data) => api.put('/settings', data);
export const getLenders = () => api.get('/lenders');

// Logo / Branding
export const uploadLogo = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/settings/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const deleteLogo = () => api.delete('/settings/logo');
export const getPublicLogo = () => api.get('/settings/logo');

// Admin
export const resetDatabase = () => api.post('/admin/reset-database');

// Users
export const getUsers = () => api.get('/users');
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const createUser = (data) => api.post('/users/create', data);
export const deleteUser = (id) => api.delete(`/users/${id}`);

// Dashboard
export const getDashboardStats = (params = {}) => api.get('/dashboard/stats', { params });

// CSV Upload
export const uploadCSV = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/upload/csv', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// AI
export const aiAnalyze = (dealId, action, context, language) => api.post('/ai/analyze', { deal_id: dealId, action, context, language });
export const generateInterviewMessage = (dealId, messageType, language = 'English') => api.post(`/ai/interview-message?deal_id=${dealId}&message_type=${messageType}&language=${language}`);

// Customer Messaging
export const sendCustomerSMS = (dealId, message, messageType) => api.post('/messaging/send-sms', { deal_id: dealId, message, message_type: messageType });
export const sendCustomerEmail = (dealId, message, messageType) => api.post('/messaging/send-email', { deal_id: dealId, message, message_type: messageType });

// Escalations
export const getEscalations = (params) => api.get('/escalations', { params });
export const getActiveEscalations = () => api.get('/escalations/active');
export const getEscalationStats = () => api.get('/escalations/stats');
export const takeEscalationAction = (id, action, notes) => api.post(`/escalations/${id}/action`, { action, notes });
export const triggerEscalationCheck = () => api.post('/escalations/check');
export const dismissEscalation = (id) => api.delete(`/escalations/${id}`);

// Email
export const testEmailIntegration = () => api.post('/email/test');
export const sendEscalationDigest = () => api.post('/email/send-escalation-digest');
export const sendDailyDigest = () => api.post('/email/daily-digest');
export const previewDailyDigest = () => api.get('/email/daily-digest/preview');
export const sendWeeklyReport = () => api.post('/email/weekly-report');
export const previewWeeklyReport = () => api.get('/email/weekly-report/preview');

// Customer Upload (no auth required - uses session token)
export const customerVerifyPhone = (phone) => api.post('/customer-upload/verify-phone', { phone });
export const customerSendPin = (phone, deal_id) => api.post('/customer-upload/send-pin', { phone, deal_id });
export const customerVerifyPin = (phone, pin, deal_id) => api.post('/customer-upload/verify-pin', { phone, pin, deal_id });
export const getCustomerDocuments = (sessionToken) => api.get('/customer-upload/documents', { headers: { 'X-Session-Token': sessionToken } });
export const uploadCustomerDocument = (documentType, file, sessionToken) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/customer-upload/upload/${encodeURIComponent(documentType)}`, formData, {
    headers: { 'X-Session-Token': sessionToken, 'Content-Type': 'multipart/form-data' }
  });
};
export const getCustomerFile = (fileId, sessionToken) => api.get(`/customer-upload/file/${fileId}`, { headers: { 'X-Session-Token': sessionToken }, responseType: 'blob' });

// Document Review (staff)
export const getPendingDocuments = () => api.get('/documents/pending-review');
export const reviewDocument = (documentId, action, reason) => api.post(`/documents/${documentId}/review`, { action, reason });
export const sendUploadLink = (dealId) => api.post(`/deals/${dealId}/send-upload-link`);

export default api;
